import type { JSX } from 'react';
import type { CSSProperties, ReactNode } from 'react';

export interface TabItem<K extends string = string> {
  key: K;
  label: ReactNode;
  icon?: ReactNode;
  /** Счётчик-бейдж (0/undefined — не показывается). */
  badge?: number | string | null;
  /** Цвет бейджа; по умолчанию info-синий, для «требует действия» — оранжевый. */
  badgeColor?: string;
}

export interface TabsProps<K extends string = string> {
  items: TabItem<K>[];
  value: K;
  onChange: (key: K) => void;
  /** Слот справа от вкладок (фильтр, кнопка). */
  right?: ReactNode;
  style?: CSSProperties;
}

/**
 * Вкладки HUB 2.0 (порт `_ds/components/Tabs`): оранжевое подчёркивание,
 * бейджи-счётчики, горизонтальный скролл на телефоне. Только переключатель —
 * содержимое вкладки страница рисует сама (так проще держать ?tab= в URL).
 */
export function Tabs<K extends string = string>({ items, value, onChange, right, style }: TabsProps<K>): JSX.Element {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, ...style }}>
      <div className="fk-ds-tabs" role="tablist" style={{ flex: 1, minWidth: 0 }}>
        {items.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            className="fk-ds-tab"
            aria-selected={t.key === value}
            onClick={() => onChange(t.key)}
          >
            {t.icon}
            {t.label}
            {t.badge ? (
              <span className="fk-ds-tab-badge" style={{ background: t.badgeColor ?? 'var(--fk-info)' }}>
                {t.badge}
              </span>
            ) : null}
          </button>
        ))}
      </div>
      {right}
    </div>
  );
}
